import { ActionsTableMenu } from '@/components/atoms/actions-table-menu';
import { Status } from '@/components/atoms/status';
import AvatarWithInfo from '@/components/molecules/avatar-with-info';
import { statusVariants, statusVisitorVariants } from '@/constans';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { VisitorMeili } from '@/features/visitors/api/types/visitor';
import {
  VisitorNotificationMethod,
  VisitorState,
  VisitState,
} from '@/features/visits/api/types/visits';
import { getConfirmationStatus } from '@/utils/get-confirmation-status';
import { getHostDisplayData } from '@/utils/get-host-display-data';
import { getTranslationBasedOnLanguage } from '@/utils/get-translation-based-on-language';
import { Box, Link, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  visitor: VisitorMeili;
  isVisitorActionInProgress: boolean;
  openConfirmDialog: (payload: { visitId: number; visitorFullName: string }) => void;
  openRejectDialog: (payload: { visitId: number; visitorFullName: string }) => void;
  resendVisitorInvitation: (payload: {
    visitorId: number;
    method: VisitorNotificationMethod;
  }) => void;
}

export const VisitorsTableRow = ({
  visitor,
  isVisitorActionInProgress,
  openConfirmDialog,
  openRejectDialog,
  resendVisitorInvitation,
}: Props) => {
  const { t, i18n } = useTranslation(['visit', 'visits', 'common']);
  const combinedDestinations = [
    ...visitor.visitAdditionalDestinations,
    visitor.visitMainDestination,
  ];

  const hostConfirmationStatus = getConfirmationStatus({
    hostConfirmed: visitor?.visitHostConfirmed,
    deviceConfirmed: visitor?.visitDeviceConfirmed,
    visitState: visitor?.visitState,
  });
  const { hostName, hostTooltip, hostAvatarText, hostEmail } = getHostDisplayData({
    host: visitor.visitHost,
    kioskEnteredHostName: visitor.visitKioskEnteredHostName,
  });

  const statusVariant = statusVariants.find(variant => variant.value === visitor.visitState);
  const statusVariantOnVisitor = statusVisitorVariants.find(
    variant => variant.value === visitor.state,
  );

  const actionsSlot = useCallback(
    (visitorId: number) => {
      const actions = [
        {
          key: 'confirm',
          label: t('common:button.confirm'),
          onClick: () =>
            openConfirmDialog({
              visitId: visitor.visitId,
              visitorFullName: `${visitor.firstName} ${visitor.lastName}`,
            }),
          disabled: isVisitorActionInProgress,
          hidden:
            visitor?.visitHostConfirmed !== false || visitor?.visitState === VisitState.REJECTED,
        },
        {
          key: 'reject',
          label: t('common:button.cancel'),
          onClick: () =>
            openRejectDialog({
              visitId: visitor.visitId,
              visitorFullName: `${visitor.firstName} ${visitor.lastName}`,
            }),
          disabled: isVisitorActionInProgress,
          hidden:
            visitor?.visitHostConfirmed !== false || visitor?.visitState === VisitState.REJECTED,
        },
        {
          key: 'resend-invitation',
          label: t('visit:resendInvitation.title'),
          onClick: () => {
            resendVisitorInvitation({
              visitorId,
              method: VisitorNotificationMethod.EMAIL,
            });
          },
          hidden:
            visitor?.visitState !== VisitState.CREATED &&
            visitor?.visitState !== VisitState.STARTED,
        },
      ];

      return <ActionsTableMenu actions={actions} />;
    },
    [
      isVisitorActionInProgress,
      openConfirmDialog,
      openRejectDialog,
      resendVisitorInvitation,
      t,
      visitor.firstName,
      visitor.lastName,
      visitor.visitHostConfirmed,
      visitor.visitId,
      visitor.visitState,
    ],
  );

  return (
    <TableRow hover key={visitor.id}>
      <TableCell>
        <Link
          color="primary"
          underline="none"
          component={RouterLink}
          to={`${visitor.visitId}?visitorId=${visitor.id}`}
          variant="inherit">
          <AvatarWithInfo
            avatarText={`${visitor.firstName.slice(0, 1)} ${visitor.lastName.slice(0, 1)}`}
            primaryText={`${visitor.firstName} ${visitor.lastName}`}
            secondaryText={visitor.email}
            src={''}
            isHandicapped={visitor.isHandicapped}
          />
        </Link>
      </TableCell>
      <TableCell>
        <Tooltip title={hostTooltip}>
          <span>
            <AvatarWithInfo
              avatarText={hostAvatarText}
              primaryText={hostName}
              secondaryText={hostEmail}
              src={''}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography color="textPrimary" variant="inherit">
          {visitor?.visitSource ? t(`visits:source.${visitor.visitSource}`) : t('common:notAdded')}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color="inherit" variant="inherit">
          {hostConfirmationStatus ? t(hostConfirmationStatus) : 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        {getTranslationBasedOnLanguage(
          visitor.visitCategory?.translations,
          i18n.language as OrganizationLanguage,
        )}
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          {visitor.visitAdditionalDestinations.length !== 0 ? (
            <Tooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {combinedDestinations.map((destination, index) => (
                    <span key={index}>
                      {destination.name} {t('visits:table.cell.floor')}: {destination.floor || '-'}
                    </span>
                  ))}
                </Box>
              }>
              <span>{`${combinedDestinations.length} ${
                combinedDestinations.length === 1
                  ? t('visits:table.destination')
                  : t('visits:table.destinations')
              }`}</span>
            </Tooltip>
          ) : (
            <>
              <Typography
                sx={{ display: 'flex', alignItems: 'center' }}
                color="text.primary"
                variant="inherit">
                {visitor.visitMainDestination.name}
              </Typography>
              <Typography color="textSecondary" variant="inherit">
                {t('visits:table.cell.floor')}: {visitor.visitMainDestination.floor}
              </Typography>
            </>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography color="inherit" variant="inherit">
          {format(new Date(visitor.visitExpectedAt), 'dd MMM yyyy')}
        </Typography>
        <Typography color="textSecondary" variant="inherit">
          {format(new Date(visitor.visitExpectedAt), 'HH:mm')}
        </Typography>
      </TableCell>
      <TableCell>
        <Status
          color={statusVariant?.color ?? 'rgba(6, 70, 153, 1)'}
          label={t(`common:statusVariants.${statusVariant?.value ?? VisitState.CREATED}`)}
        />
      </TableCell>
      <TableCell>
        <Status
          color={statusVariantOnVisitor?.color ?? 'rgba(6, 70, 153, 1)'}
          label={t(
            `common:statusVariantVisitor.${statusVariantOnVisitor?.value ?? VisitorState.INVITED}`,
          )}
        />
      </TableCell>
      <TableCell>{actionsSlot(Number(visitor.id))}</TableCell>
    </TableRow>
  );
};
